import { Paper, TableBody, TableContainerProps, TableHead, TableProps, TableRow } from "@mui/material";
import React from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import { getTranslateByKey, useResponsiveTable } from "@/main/services/utils";
import { ResolutionsEnum } from "@/main/services/enum/ResolutionsEnum";
import { isNullUndefined } from "@/main/services/reducerUtils";

import LoadingComponent from "../loading/loadingComponent";
import * as S from "./styles";

interface IBoxProps extends TableContainerProps {
	title?: string;
	maxHeight?: string;
	marginCustom?: string;
	typeGrid?: "oddEven" | "default" | "backgroundHeader" | "backgroundBody";
	sizeCell?: "small" | "medium" | "large";
	textAlign?: "center" | "left" | "right";
	rowHeaders?: (string | null)[] | (React.ReactNode | null)[];
	centerLastColumn?: boolean;
	children: React.ReactNode;
	propsTable?: TableProps;
	infiniteScrollConfig?: {
		hasMore: boolean;
		fetchData: () => void;
		dataLength: number;
		isLoading: boolean;
		idScrollTarget?: string;
		disableScroll?: boolean;
		scrollThreshold?: number | string;
		marginLoader?: string;
	} | null;
	sizeColumns?: (string | null)[];
	widthCustom?: string;
	breakpointTable?: number;
	showEndMessage?: boolean;
	backgroundHeader?: string;
}

function KOGBoxGrid({
	title,
	maxHeight = "",
	marginCustom = "",
	rowHeaders,
	children,
	infiniteScrollConfig,
	sizeCell = "large",
	typeGrid = "default",
	textAlign = "left",
	centerLastColumn = false,
	propsTable,
	sizeColumns,
	widthCustom,
	breakpointTable = ResolutionsEnum.tablet,
	showEndMessage = true,
	backgroundHeader,
	...props
}: IBoxProps) {
	const filterSizeColumns = sizeColumns?.filter((size) => size !== null);
	const filterRowHeaders = rowHeaders?.filter((header) => header !== null);

	const { totalWidthPercent, totalWidthPX } = useResponsiveTable(filterSizeColumns);

	function renderGrid() {
		return (
			<S.StyledTableContainer id="id-general-boxgrid" component={Paper} $maxHeight={maxHeight} {...props}>
				<S.StyledTable
					{...propsTable}
					$typeGrid={typeGrid}
					$sizeCell={sizeCell}
					$isFixedTable={!isNullUndefined(filterSizeColumns)}
					$breakpointTable={breakpointTable}
					$backgroundHeader={backgroundHeader}
					aria-label="simple table"
					size="small"
				>
					{!isNullUndefined(filterSizeColumns) && (
						<colgroup>
							{filterSizeColumns.map((size, index) => (
								<col
									key={index}
									style={{
										width: size === "responsive" ? `calc(100% - ${totalWidthPercent}% - ${totalWidthPX}px)` : size,
									}}
								/>
							))}
						</colgroup>
					)}
					<TableHead>
						<TableRow>
							{filterRowHeaders &&
								filterRowHeaders.map((header, index) =>
									index === 0 ? (
										<S.KOGTableCell
											key={index}
											style={{
												padding: sizeCell == "small" ? "2px 8px" : "2px 16px",
											}}
										>
											{header}
										</S.KOGTableCell>
									) : index === filterRowHeaders.length - 1 && centerLastColumn === true ? (
										<S.KOGTableCell
											key={index}
											style={{
												textAlign: "center",
												padding: sizeCell == "small" ? "2px 8px" : "2px 16px",
											}}
										>
											{header}
										</S.KOGTableCell>
									) : (
										<S.KOGTableCell
											key={index}
											style={{
												textAlign,
												padding: sizeCell == "small" ? "2px 8px" : "2px 16px",
											}}
										>
											{header}
										</S.KOGTableCell>
									),
								)}
						</TableRow>
					</TableHead>
					<TableBody>{children}</TableBody>
				</S.StyledTable>
			</S.StyledTableContainer>
		);
	}

	return (
		<S.Container $marginCustom={marginCustom} $widthCustom={widthCustom}>
			{title && <S.Title>{title}</S.Title>}

			{infiniteScrollConfig ? (
				<div
					style={{
						height: infiniteScrollConfig.idScrollTarget ? "auto" : "95%",
					}}
				>
					<InfiniteScroll
						style={{
							overflowX: "hidden",
							overflowY: infiniteScrollConfig.disableScroll ? "hidden" : "auto",
						}}
						dataLength={infiniteScrollConfig.dataLength}
						next={infiniteScrollConfig.fetchData}
						hasMore={infiniteScrollConfig.hasMore}
						scrollableTarget={infiniteScrollConfig.idScrollTarget}
						scrollThreshold={infiniteScrollConfig.scrollThreshold}
						loader={infiniteScrollConfig.isLoading ? <LoadingComponent /> : <></>}
						endMessage={
							showEndMessage ? (
								<S.EndMessage className="d-flex d-flex justify-content-center">
									{getTranslateByKey("information.allResultsWereDisplayed")}
								</S.EndMessage>
							) : null
						}
					>
						{renderGrid()}
					</InfiniteScroll>
				</div>
			) : (
				renderGrid()
			)}
		</S.Container>
	);
}

export default KOGBoxGrid;
